import { Injectable } from '@angular/core';
import { AngularFirestore, QuerySnapshot } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { LecturePriorityEnum, LectureStatusEnum } from 'src/app/submit-lecture/lecture.values';
import { UserAdminVerificationStatusEnum } from 'src/app/users/interfaces/user-values';
import { IFilter } from 'src/app/views/pages/lectures/find-lecture/components';
import { ILecture } from 'src/app/views/pages/lectures/lecture.interface';
import { IAuditLog } from '../../audit-log/audit-log.interface';
import { EntityTypeEnum } from '../../audit-log/entity-type.enum';

@Injectable({
  providedIn: 'root',
})
export class FirestoreRulesService {

  constructor(private readonly firestore: AngularFirestore) {
    // firebase.firestore().enablePersistence()
    //     .then(() => console.log('enable cache mode successfully'))
    //     .catch(err => console.log(`enable cache mode error: ${err}`));
  }

  // TODO avraham get collection names from env variables with Niv
  private readonly PERMISSIONS = 'rolesToUsers';
  private readonly LECTURES = 'lectures';
  private readonly USERS_META_DATA = 'users-meta';
  private readonly SKILLS_PREF = 'skills-prefs';
  private readonly AUDIT_LOG = 'auditLog';
  private readonly REMINDERS = 'reminders';

  private deleteDocument(collectionName: string, primaryDocId: string): Promise<void> {
    return this.firestore.collection(collectionName).doc(primaryDocId).delete();
  }

  private updateDocument(collectionName: string, primaryDocId: string, updatedDocument: unknown): Promise<void> {
    return this.firestore.collection(collectionName).doc(primaryDocId).set(updatedDocument, { merge: true });
  }

  private updateInnerDocument(
    primaryCollectionName: string,
    primaryDocId: string,
    secondaryCollectionName: string,
    secondaryDocId: string,
    updatedDocument: any): Promise<void> {
    return this.firestore.collection(primaryCollectionName).doc(primaryDocId).collection(secondaryCollectionName)
      .doc(secondaryDocId).set(updatedDocument, { merge: true });
  }

  private getAllDocuments(collectionName: string, useCache: boolean = false, query = undefined): Promise<any> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(collectionName, query)
        .get({ source: useCache ? 'cache' : 'default' })
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          }));
        },
          (err) => {
            reject(err);
          });
    });
  }

  // Gets
  getLastChangedLecture(): Promise<ILecture[]> {
    const query = this.firestore.collection(this.LECTURES).ref
      .orderBy('updated_at', 'desc').limit(1);
    return this.getQueryPromise(query);
  }

  getLastChangedUser(): Promise<ILecture[]> {
    const query = this.firestore.collection(this.USERS_META_DATA).ref
      .orderBy('updated_at', 'desc').limit(1);
    return this.getQueryPromise(query);
  }

  getLecturesByUser(uid: string, useCache: boolean): Promise<any[]> {
    const query = (this.firestore.collection(this.LECTURES).ref
      .where('assigned_uid', '==', uid)
      .where('status', 'not-in', [LectureStatusEnum.IRRELEVANT, LectureStatusEnum.COMPLETE]) as any);
    return this.getQueryPromise(query, useCache);
  }

  getLecturesByUserAndStatus(uid: string, status: LectureStatusEnum, useCache: boolean): Promise<any[]> {
    const query = (this.firestore.collection(this.LECTURES).ref
      .where('assigned_uid', '==', uid).where('status', '==', status) as any);
    return this.getQueryPromise(query, useCache);
  }

  getLectureById(docId: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore.collection(this.LECTURES).doc(docId).valueChanges({ idField: 'docId' })
        .subscribe(doc => {
          const lecture = doc as any;
          lecture.metadata = { docId };
          resolve(lecture);
        },
          (err) => {
            reject(err);
          });
    });
  }

  getLecturesInDates(startDate: Date, endDate: Date, field: string, useCache: boolean): Promise<any[]> {
    const query = (this.firestore.collection(this.LECTURES).ref
      .where(field, '>=', startDate).where(field, '<=', endDate) as any);
    return this.getQueryPromise(query, useCache);
  }

  getAllLectures(useCache: boolean, query = undefined): Promise<ILecture[]> {
    return this.getAllDocuments(this.LECTURES, useCache, query);
  }

  getLecturesByStatus(status: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.LECTURES, ref => ref.where('status', '==', status))
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          })
          );
        },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getLecturesBySkyzID(skyzId: string, useCache = true): Promise<unknown> {
    const query = this.firestore.collection(this.LECTURES).ref
      .where('skyz_id', '==', skyzId);
    return this.getQueryPromise(query, useCache);
  }

  getLecturesByStatusAndFilter(status: LectureStatusEnum, filterValues: IFilter): Promise<unknown> {
    if (filterValues.prefs) {
      const promises = [];
      const locations = filterValues.prefs.locations.value;
      if (locations && locations.length > 0) {
        if (locations.length > 10) {
          const first = locations.slice(0, 10);
          const second = locations.slice(10);

          let query1 = null;
          if (!(filterValues as any).paid) {
            query1 = this.firestore.collection(this.LECTURES).ref.where('status', '==', status).where('type', '==', 'NONPAID');
          } else {
            query1 = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
          }

          query1 = (query1.where(filterValues.prefs.locations.key, 'in', first) as any);
          promises.push(this.getQueryPromise(query1));

          let query2 = null;
          if (!(filterValues as any).paid) {
            query2 = this.firestore.collection(this.LECTURES).ref.where('status', '==', status).where('type', '==', 'NONPAID');
          } else {
            query2 = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
          }

          query2 = (query2.where(filterValues.prefs.locations.key, 'in', first) as any);
          promises.push(this.getQueryPromise(query2));

        } else {
          let query = null;
          if (!(filterValues as any).paid) {
            query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status).where('type', '==', 'NONPAID');
          } else {
            query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
          }

          query = (query.where(filterValues.prefs.locations.key, 'in', locations) as any);
          promises.push(this.getQueryPromise(query));

        }

      }
      const ages = filterValues.prefs.age_groups.value;
      if (ages && ages.length > 0) {
        let query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
        query = (query.where(filterValues.prefs.age_groups.key, 'in', ages) as any);
        promises.push(this.getQueryPromise(query));
      }

      const languages = filterValues.prefs.languages.value;
      if (languages && languages.length > 0) {
        let query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
        query = (query.where(filterValues.prefs.languages.key, 'in', languages) as any);
        promises.push(this.getQueryPromise(query));
      }

      return Promise.all(
        promises
      );

    } else {
      const promises = [];
      filterValues.exact.forEach((item) => {
        if (item.value.length > 0) {
          let query = null;
          if (!(filterValues as any).paid) {
            query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status).where('type', '==', 'NONPAID');
          } else {
            query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
          }
          query = (query.where(item.key, 'in', item.value) as any);
          promises.push(this.getQueryPromise(query));
        }
      });

      if (filterValues.freeText?.value && filterValues.freeText?.value.length > 0) {
        let query = null;
        if (!(filterValues as any).paid) {
          query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status).where('type', '==', 'NONPAID');
        } else {
          query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
        }
        query = (query.where(filterValues.freeText.key, '==', filterValues.freeText.value) as any);
        promises.push(this.getQueryPromise(query));
      }
      return Promise.all(
        promises
      );
    }
  }

  getAllUsersMeta(useCache: boolean, query?: any): Promise<ILecture[]> {
    return this.getAllDocuments(this.USERS_META_DATA, useCache, query);
  }

  private getQueryPromise(query: any, useCache: boolean = false): Promise<any> {
    return new Promise((resolve, reject) => {
      (query.get({ source: useCache ? 'cache' : 'default' }) as any).then((querySnapshot) => {
        const docs = querySnapshot.docs.map((doc) => {
          const data: any = doc.data();
          data.metadata = {
            docId: doc.id
          };
          return data;
        });
        resolve(docs);
      },
        (err) => {
          reject(err);
        }
      );
    });
  }

  getLecturesByStatusAndPriority(status: LectureStatusEnum, priority: LecturePriorityEnum, useCache): Promise<any[]> {
    let query = this.firestore.collection(this.LECTURES).ref.where('status', '==', status);
    query = (query.where('priority', '==', priority));
    return this.getQueryPromise(query, useCache);
  }

  getLecturesByPriority(priority: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.LECTURES, ref => ref.where('priority', '==', priority))
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          })
          );
        },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getAllUsers(useCache: boolean, query = undefined): Promise<any[]> {
    return this.getAllDocuments(this.USERS_META_DATA, useCache, query);
  }

  getUserMetadata(userId: string): Observable<any> {
    return this.firestore.collection(this.USERS_META_DATA).doc(userId).valueChanges({ idField: 'docId' });
  }

  getUsersMetadata(uids: string[]): Observable<QuerySnapshot<unknown>> {
    return this.firestore.collection(this.USERS_META_DATA, (ref) => ref.where('docId', 'array-contains', uids)).get();
  }

  getUserPreferences(userId: string): Observable<{ docId: string }> {
    return this.firestore.collection(this.USERS_META_DATA).doc(userId)
      .collection(this.SKILLS_PREF).doc('preferences').valueChanges({ idField: 'docId' });
  }

  isUserAdmin(uid: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.firestore.collection(this.PERMISSIONS).doc('admin').collection('uids').doc(uid).valueChanges({ idField: 'docId' })
        .subscribe(
          (role: unknown) => resolve(true),
          (err) => {
            resolve(false);
          }
        );
    });
  }

  getisUserAdmin(uid: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!uid) {
        resolve(false);
      }
      this.firestore
        .collection(this.PERMISSIONS).doc('admin').collection('uids').doc(uid)
        .get()
        .subscribe(querySnapshot => {
          const existsInAdmin = querySnapshot.exists;
          resolve(existsInAdmin);
        },
        (err) => {
          reject(err);
        });
    });
  }

  getAllAuditLogs(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.AUDIT_LOG)
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.id = doc.id;
            return data;
          }));
        },
          (err) => {
            reject(err);
          });
    });
  }

  getAuditLogsByEntityId(uid: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.AUDIT_LOG, (ref) => ref.where('entityId', '==', uid))
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          }));
        },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getAuditLogsByEntityType(entityType: EntityTypeEnum): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.AUDIT_LOG, (ref) => ref.where('entityType', '==', entityType))
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          }));
        },
          (err) => {
            reject(err);
          }
        );
    });
  }

  // adds
  addUserToRoles(uid: string): Promise<void> {
    return this.firestore.collection(this.PERMISSIONS).doc('user').collection('uids').doc(uid).set({});
  }

  addUserToAdmin(uid: string): Promise<void> {
    return this.firestore.collection(this.PERMISSIONS).doc('admin').collection('uids').doc(uid).set({});
  }

  removeUserFromRoles(uid: string): Promise<void> {
    return this.firestore.collection(this.PERMISSIONS).doc('user').collection('uids').doc(uid).delete();
  }

  removeUserFromAdmin(uid: string): Promise<void> {
    return this.firestore.collection(this.PERMISSIONS).doc('admin').collection('uids').doc(uid).delete();
  }

  initializeUserMetadata(uid: string, metadata: any): Promise<void> {
    metadata.updated_at = new Date();
    return this.firestore.collection(this.USERS_META_DATA).doc(uid)
      .set(metadata);
  }

  addUserMetadata(uid: string): Promise<void> {
    const metadata: any = {};
    metadata.updated_at = new Date();
    metadata.created_at = new Date();
    return this.firestore.collection(this.USERS_META_DATA).doc(uid).set(metadata);
  }

  addLecture(doc: any): Promise<any> {
    return this.firestore.collection(this.LECTURES).add(doc)
      .then((docRef) => {
        // this.cacheService.resetCache(); // TODO disabling cache
        return docRef.id;
      })
      .catch((error) => {
        console.error('Error adding document: ', error);
        throw error;
      });
  }

  addAuditLog(doc: IAuditLog): Promise<void> {
    return this.firestore.collection(this.AUDIT_LOG).add(doc)
      .then((docRef) => {
      })
      .catch((error) => {
        console.error('Error adding document: ', error);
        throw error;
      });
  }

  // Updates
  updateLecture(lectureId: string, updatedLecture: ILecture, origLecture: ILecture): Promise<void> {
    return this.updateDocument(this.LECTURES, lectureId, updatedLecture).then(res => {
      return res;
    });
  }

  updateUserMetaData(userId: string, updatedUser: any): Promise<void> {
    updatedUser.updated_at = new Date();
    return this.updateDocument(this.USERS_META_DATA, userId, updatedUser);
  }

  updateUserPreferences(userId: string, updatedPrefs: unknown): Promise<void> {
    return this.updateInnerDocument(this.USERS_META_DATA, userId, this.SKILLS_PREF, 'preferences', updatedPrefs);
  }

  updateLectureStatus(lectureId: string, status: string): Promise<void> {
    return this.firestore
      .collection(this.LECTURES)
      .doc(lectureId)
      .update({
        status,
        uid: ''
      });
  }

  updateLectureTime(lectureId: string, time: Date): Promise<void> {
    return this.firestore
      .collection(this.LECTURES)
      .doc(lectureId)
      .update({
        scheduled_date: time
      });
  }

  markLectureComplete(lectureId: string): Promise<void> {
    return this.firestore
      .collection(this.LECTURES)
      .doc(lectureId)
      .update({
        status: 'complete'
      });
  }

  // delete
  deleteLecture(lectureId: string): Promise<void> {
    return this.deleteDocument(this.LECTURES, lectureId).then(res => {
      return res;
    });
  }

  deleteUserMetadata(userId: string): Promise<void> {
    return this.deleteDocument(this.USERS_META_DATA, userId);
  }

  getAssignedUsers(useCache: boolean = false): Promise<any[]> {
    const query = this.firestore.collection(this.USERS_META_DATA).ref
      .where('adminVerificationStatus', '==', UserAdminVerificationStatusEnum.APPROVED);
    return this.getQueryPromise(query, useCache);

  }

  getCompleteLecturesByUser(status: string, uid: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.LECTURES, ref => ref.where('status', '==', status).where('assigned_uid', '==', uid))
        .get()
        .subscribe(querySnapshot => {
          resolve(querySnapshot.docs.map((doc) => {
            const data: any = doc.data();
            data.metadata = {
              docId: doc.id
            };
            return data;
          })
          );
        },
          (err) => {
            reject(err);
          }
        );
    });
  }

  deleteReminderByUser(email: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection(this.REMINDERS, (ref) => ref.where('volunterMail', '==', email))
        .get()
        .subscribe(querySnapshot => {
          querySnapshot.docs.map((doc) => {
            console.log(`About to delete reminder ${JSON.stringify(doc.data())}`);
            doc.ref.delete();
          });
          resolve(true);
        },
          (err) => {
            reject(err);
          }
        );
    });
  }
}
